import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";
import { Check } from "lucide-react";

export default function CheckoutSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const isDonation = params.get("type") === "donation";
    const [status, setStatus] = useState("checking");
    const [data, setData] = useState(null);
    const { clear } = useCart();

    useEffect(() => {
        if (!sessionId) return;
        let attempts = 0;
        let cancelled = false;
        const poll = async () => {
            try {
                const r = await api.get(`/checkout/status/${sessionId}`);
                if (cancelled) return;
                setData(r.data);
                if (r.data.payment_status === "paid") {
                    setStatus("paid");
                    clear();
                    return;
                }
                if (r.data.status === "expired") {
                    setStatus("expired");
                    return;
                }
                if (attempts < 8) {
                    attempts += 1;
                    setTimeout(poll, 2000);
                } else {
                    setStatus("timeout");
                }
            } catch {
                setStatus("error");
            }
        };
        poll();
        return () => { cancelled = true; };
        // eslint-disable-next-line
    }, [sessionId]);

    const renderBody = () => {
        if (status === "paid") {
            return (
                <>
                    <div className="w-20 h-20 rounded-full bg-[#D4AF37] flex items-center justify-center mx-auto mb-8">
                        <Check size={36} color="#0A0A0A" strokeWidth={2} />
                    </div>
                    <div className="eyebrow text-[#B8860B] mb-4">{isDonation ? "Donation Received" : "Order Confirmed"}</div>
                    <h1 className="font-serif text-5xl tracking-tight mb-6">Thank you.</h1>
                    <p className="text-black/65 font-light mb-10 [font-variant-ligatures:none]">
                        {isDonation
                            ? "Your generosity keeps the microphones on and our programs moving. The Gift of Kindness Podcast is a 501(c)(3) nonprofit — your gift is tax-deductible, and Stripe has emailed your receipt."
                            : "We've received your order. A confirmation has been sent to your inbox. 10% of every purchase supports listener-nominated nonprofits — including yours."}
                    </p>
                    {data?.amount_total != null && (
                        <p data-testid="success-amount" className="text-sm text-black/60 mb-8">
                            {isDonation ? "Gift amount" : "Total paid"}: ${(data.amount_total / 100).toFixed(2)} {data.currency?.toUpperCase()}
                        </p>
                    )}
                    {isDonation
                        ? <Link to="/" className="kind-btn-primary">Back to Home</Link>
                        : <Link to="/shop" className="kind-btn-primary">Continue Shopping</Link>}
                </>
            );
        }
        if (status === "checking") {
            return (
                <>
                    <div className="eyebrow text-[#B8860B] mb-6">One moment…</div>
                    <h1 className="font-serif text-4xl mb-4">Confirming your kindness</h1>
                    <p className="text-black/60 font-light">Verifying payment status…</p>
                </>
            );
        }
        return (
            <>
                <h1 className="font-serif text-4xl mb-4">Something is taking longer than expected</h1>
                <p className="text-black/60 font-light mb-8">
                    {isDonation
                        ? "We couldn't confirm your donation yet. If it went through, Stripe will email your receipt shortly."
                        : "Please check your email for a receipt, or contact us."}
                </p>
                {isDonation
                    ? <Link to="/" className="kind-btn-secondary">Back to Home</Link>
                    : <Link to="/shop" className="kind-btn-secondary">Back to Shop</Link>}
            </>
        );
    };

    return (
        <div data-testid="checkout-success" className="px-6 py-28 max-w-2xl mx-auto text-center">
            {renderBody()}
        </div>
    );
}
